import debounce from '../utils/debounce';
import throttle from '../utils/throttle';

export default class VerticalVideoCarousel {
  constructor(elem) {
    this.elem = elem;
    this.carousel = this.elem.querySelector(".js-slider");
    this.slides = this.elem.querySelectorAll(".js-slide");
    this.videoTags = this.elem.querySelectorAll("video");
    this.deviceType = null;
    this.previousDeviceType = null;
    this.observers = [];
    this.nextButton = this.elem.querySelector(".js-next");
    this.prevButton = this.elem.querySelector(".js-prev");
    this.activeSlide = this.slides[0];
  }

  setupCarousel() {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: .5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // pause the active slide video, if any
          const activeVideo = this.activeSlide.querySelector("video");
          if (activeVideo) {
            activeVideo.pause();
          }

          this.activeSlide.classList.remove("js-slide-active");
          this.activeSlide = entry.target;
          this.activeSlide.classList.add("js-slide-active");

          // play the video of the active slide, if any
          const video = entry.target.querySelector("video");
          if (!video) return;
          video.play();
        }
      });
    }, observerOptions);

    this.slides.forEach((slide) => {
      observer.observe(slide);
      this.observers.push(observer);
    });
  }

  slideHoverHandler(e) {
    if (this.deviceType === 'mobile') return;

    const isLeave = e.type === "mouseleave";
    const slide = e.currentTarget;
    const video = slide.querySelector("video");

    if (!video) return;

    switch (isLeave) {
      case true:
        video.pause();
        break;
      default:
        video.play();
        break;
    }
  }

  deviceEventController() {
    const isMobile = window.matchMedia('(max-width: 991px)').matches;
    const isDesktop = window.matchMedia('(min-width: 992px)').matches;

    if (isMobile) {
      this.deviceType = 'mobile';
      if (this.previousDeviceType === this.deviceType) return;
      // mobile setup, desktop teardown
      this.slides.forEach((slide, i) => {
        slide.removeEventListener("mouseenter", this.slideHoverHandler.bind(this));
        slide.removeEventListener("mouseleave", this.slideHoverHandler.bind(this));
      });

      this.nextButton.removeEventListener("click", throttle(this.handleDesktopControls.bind(this, "next"), 200));
      this.prevButton.removeEventListener("click", throttle(this.handleDesktopControls.bind(this, "prev"), 200));

      this.nextButton.addEventListener("click", throttle(this.handleMobileControls.bind(this, "next"), 200));
      this.prevButton.addEventListener("click", throttle(this.handleMobileControls.bind(this, "prev"), 200));

      this.setupCarousel();
    }
    if (isDesktop) {
      this.deviceType = 'desktop';
      if (this.previousDeviceType === this.deviceType) return;
      // desktop setup, mobile teardown
      this.slides.forEach((slide, i) => {
        slide.addEventListener("mouseenter", this.slideHoverHandler.bind(this));
        slide.addEventListener("mouseleave", this.slideHoverHandler.bind(this));
      });
      if (this.observers.length) {
        this.observers.forEach((observer) => {
          observer.disconnect();
        });
        this.observers = [];
      }

      this.videoTags.forEach((video) => {
        video.pause();
      });

      this.nextButton.removeEventListener("click", throttle(this.handleMobileControls.bind(this, "next"), 200));
      this.prevButton.removeEventListener("click", throttle(this.handleMobileControls.bind(this, "prev"), 200));

      this.nextButton.addEventListener("click", throttle(this.handleDesktopControls.bind(this, "next"), 200));
      this.prevButton.addEventListener("click", throttle(this.handleDesktopControls.bind(this, "prev"), 200));
    }

    this.previousDeviceType = isMobile ? 'mobile' : 'desktop';
  }

  handleDesktopControls(direction) {
    const viewportWidth = this.carousel.clientWidth;

    switch (direction) {
      case "next":
        this.carousel.scrollBy({
          left: viewportWidth,
          behavior: "smooth",
        });
        break;
      case "prev":
        this.carousel.scrollBy({
          left: -viewportWidth,
          behavior: "smooth",
        });
        break;
      default:
        break;
    }
  }

  handleMobileControls(direction) {
    const activeSlide = this.carousel.querySelector(".js-slide-active");
    if (!activeSlide) return;
    const targetSibling = direction === "next" ? activeSlide.nextElementSibling : activeSlide.previousElementSibling;

    if (!targetSibling) return;
    targetSibling.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }

  scrollHandler() {
    const viewportWidth = this.carousel.clientWidth;
    const slideWidth = this.slides[0].clientWidth;
    const scrollLeft = this.carousel.scrollLeft;
    const maxScroll = this.carousel.scrollWidth - viewportWidth;

    if (scrollLeft === 0 || scrollLeft <= (slideWidth / 2)) {
      this.prevButton.setAttribute("disabled", true);
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (scrollLeft === maxScroll || scrollLeft >= maxScroll - (slideWidth / 2)) {
      this.nextButton.setAttribute("disabled", true);
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }


  init() {
    this.deviceEventController();
    window.addEventListener('resize', debounce(this.deviceEventController.bind(this), 200));
    this.carousel.addEventListener("scroll", throttle(this.scrollHandler.bind(this), 100));
  }
}